/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */
import React from "react";
import Button from "@mui/material/Button";
import settlementCostsStyle from "./settlementCostsStyle";
import Grid from "@mui/material/Grid";
import { Link } from "react-router";
import CostCalculator from "../../../services/settlement/CostCalculator";
import { STATUS_CHECKED, STATUS_SUBMITTED } from "../../../services/settlement/leadStatus";
import CostHintBox from "./CostsHintBox/CostHintBox";
import { getCaseLink } from "../../../services/productService";
import { Box, Typography } from "@mui/material";
import { AbstractCase } from "../../../types/Entities/AbstractCase";

type SettlementCostsProps = {
  settlement: AbstractCase;
};

const SettlementCosts = ({ settlement }: SettlementCostsProps) => {
  if (settlement.employedSince === null) {
    return (
      <Box sx={settlementCostsStyle.noEstimate}>
        <p>
          <strong>Die Prüfung der Kündigung ist kostenlos! Wenn Sie alle Angaben gemacht haben, sehen Sie hier direkt Ihre mögliche Abfindungssumme.</strong>
        </p>
        <p>Sie möchten gegen die Kündigung vorgehen? Dann übernehmen wir alle Anwalts- und Gerichtskosten für Sie! Sie bezahlen nur bei Erfolg!</p>
        <p>Sie haben eine Rechtsschutzversicherung für Arbeitsrecht? Dann entstehen für Sie keine Kosten und wir übernehmen Ihren Selbstbehalt!</p>
        <Button component={Link} to={getCaseLink(settlement) + "/antrag"} color={"primary"}>
          Angaben vervollständigen
        </Button>
      </Box>
    );
  }

  const estimate = CostCalculator.settlementValue(settlement);
  const fee = settlement.noWinNoFee ? settlement.nWnFAmount : CostCalculator.getFullCosts(CostCalculator.calculateValueInDispute(settlement));

  const settlementLabel = settlement.noWinNoFee ? "Geschätzte Abfindung" : "Höhe der Abfindung";
  const asterisk = settlement.noWinNoFee ? "" : "*";

  return (
    <>
      <Typography
        sx={{
          textAlign: { xs: "center", md: "left" },
          paddingBottom: "20px",
          marginBottom: "16px"
        }}>
        Die Höhe der Kosten kann, basierend auf Ihren Angaben, wie folgt geschätzt werden:
      </Typography>
      <Grid container sx={settlementCostsStyle.settlementCosts}>
        <Grid
          size={{
            sm: 8,
            xs: 12
          }}>
          {settlementLabel}
        </Grid>
        <Grid
          sx={settlementCostsStyle.money}
          size={{
            sm: 4,
            xs: 12
          }}>
          {estimate.toLocaleString("de-DE", {
            style: "currency",
            currency: "EUR",
          })}
          {asterisk}
        </Grid>
        <Grid sx={settlementCostsStyle.divider} size={12} />

        {settlement.legalExpenseInsurance && (
          <>
            <Grid
              size={{
                sm: 8,
                xs: 12
              }}>
              Rechtsanwaltsgebühren inkl. MwSt
            </Grid>
            <Grid
              sx={settlementCostsStyle.moneyStrike}
              size={{
                sm: 4,
                xs: 12
              }}>
              {fee.toLocaleString("de-DE", {
                style: "currency",
                currency: "EUR",
              })}
              *
            </Grid>
          </>
        )}

        {!settlement.noWinNoFee && !settlement.legalExpenseInsurance && (
          <>
            <Grid
              size={{
                sm: 8,
                xs: 12
              }}>
              {settlement.leadStatus < STATUS_CHECKED && "Geschätzte "}Anwaltskosten
            </Grid>
            <Grid
              sx={settlementCostsStyle.money}
              size={{
                sm: 4,
                xs: 12
              }}>
              {fee.toLocaleString("de-DE", {
                style: "currency",
                currency: "EUR",
              })}
              *
            </Grid>
          </>
        )}

        {settlement.noWinNoFee && !settlement.legalExpenseInsurance && (
          <>
            <Grid
              size={{
                sm: 8,
                xs: 12
              }}>
              Ihre Kosten bei Erfolg
            </Grid>
            <Grid
              sx={settlementCostsStyle.money}
              size={{
                sm: 4,
                xs: 12
              }}>
              {fee.toLocaleString("de-DE", {
                style: "currency",
                currency: "EUR",
              })}
            </Grid>
          </>
        )}

        {settlement.noWinNoFee && settlement.leadStatus >= STATUS_SUBMITTED ? null : <CostHintBox settlement={settlement} />}

        {!settlement.noWinNoFee && (
          <>
            <Grid sx={settlementCostsStyle.divider} size={12} />
            <Grid sx={settlementCostsStyle.postfix} size={12}>
              *
              {estimate === 0 && (
                <span>
                  Wenn Sie unter einem Jahr im Unternehmen beschäftigt waren, ist Ihre erwartete Abfindung geringer als ein halbes Bruttomonatsgehalt. Die
                  Faustformel für die Berechnung lautet 0,5xBruttomonatsgehalt pro Geschäftsjahr.{" "}
                </span>
              )}
              Dies sind Schätzungen. Die tatsächliche Höhe der Abfindung und der Kosten variiert je nach Verlauf des Verfahrens.
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
};

export default SettlementCosts;
